<template>
  <div class="accordion" :id="accordionOuterId">
    <div class="accordion-item accordion-layout">
      <div class="d-flex justify-content-between align-items-center">
        <div class="form-check form-switch d-flex align-items-center">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            v-model="isActive"
            :disabled="disabled"
            :id="filterName"
          />
          <label class="form-check-label NKT-P1" :for="filterName">{{ title }}</label>
        </div>
        <div>
          <MsButton
            :disabled="disabled"
            data-bs-toggle="collapse"
            :data-bs-target="'#' + accordionInnerId"
            aria-expanded="false"
            :aria-controls="accordionInnerId"
          >
            <img
              :src="disabled ? lock : arrow"
              :class="{ rotated: isExpanded && !disabled }"
              class="icon"
            />
          </MsButton>
        </div>
      </div>

      <DAS
        :accordion-inner-id="accordionInnerId"
        :accordion-outer-id="accordionOuterId"
        v-if="title === 'DAS'"
      />
      <DoB
        :accordion-inner-id="accordionInnerId"
        :accordion-outer-id="accordionOuterId"
        v-if="title === 'DoB'"
      />
      <DTS
        :accordion-inner-id="accordionInnerId"
        :accordion-outer-id="accordionOuterId"
        v-if="title === 'DTS'"
      />
      <Vesseltraffic
        :accordion-inner-id="accordionInnerId"
        :accordion-outer-id="accordionOuterId"
        v-if="title === 'Vessel traffic'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, computed, onMounted, onBeforeUnmount } from 'vue'
import { useFilterStore } from '@/stores/HomeFilterStore'
import MsButton from '@/components/general/buttons/MsButton.vue'
import DAS from './DAS.vue'
import DoB from './DoB.vue'
import DTS from './DTS.vue'
import Vesseltraffic from './Vesseltraffic.vue'
import lock from '@/assets/svg/lock-gray.svg'
import arrow from '@/assets/svg/arrow.svg'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const filterStore = useFilterStore()

const filterName = ref('filter-' + props.title.replace(/ /g, ''))
const accordionInnerId = ref('accordion-filter-' + props.title.replace(/ /g, ''))
const accordionOuterId = ref('dropdown-filter-' + props.title.replace(/ /g, ''))

const isActive = computed({
  get() {
    if (props.disabled) {
      return false
    }

    if (props.title === 'DTS') {
      return filterStore.isDTSActive
    } else if (props.title === 'DoB') {
      return filterStore.isDoBActive
    } else if (props.title === 'DAS') {
      return filterStore.isDASActive
    } else if (props.title === 'Vessel traffic') {
      return filterStore.isVesselActive
    }
    return false
  },
  set() {
    if (props.title === 'DTS') {
      filterStore.setDTS()
    } else if (props.title === 'DoB') {
      filterStore.setDoB()
    } else if (props.title === 'DAS') {
      filterStore.setDAS()
    } else if (props.title === 'Vessel traffic') {
      filterStore.setVessel()
    }
  }
})

const isExpanded = ref(false)

const updateExpandedState = (expanded: boolean) => {
  isExpanded.value = expanded
}

onMounted(() => {
  const element = document.getElementById(accordionInnerId.value)
  if (element) {
    element.addEventListener('show.bs.collapse', () => updateExpandedState(true))
    element.addEventListener('hide.bs.collapse', () => updateExpandedState(false))
  }
})

onBeforeUnmount(() => {
  const element = document.getElementById(accordionInnerId.value)
  if (element) {
    element.removeEventListener('show.bs.collapse', () => updateExpandedState(true))
    element.removeEventListener('hide.bs.collapse', () => updateExpandedState(false))
  }
})
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.accordion {
  border: none;
}

.accordion-item {
  border: none;
}

.accordion-layout {
  width: 192px;

  border-radius: var(--border-radius-small-tryout, 4px);
  background: color.$color-white;

  /* Dropeffect */
  box-shadow: 0px 0px 0.5px 0px rgba(66, 71, 76, 0.32), 0px 4px 8px 0px rgba(66, 71, 76, 0.05);
}

.form-check-input {
  background-color: color.$color-medium-grey;
  border-color: color.$color-medium-grey;
  transform: scale(0.5);
}

.form-check-input:checked {
  background-color: color.$color-offshore-violet;
  border-color: color.$color-offshore-violet;
}

.form-check-input:disabled {
  background-color: color.$color-dark-grey;
  border-color: color.$color-dark-grey;
}

.form-check-input:focus:not(:checked) {
  background-color: color.$color-medium-grey;
}
</style>
